import { useCallback, useMemo, useRef, useState } from "react";
import { Badge } from "react-bootstrap";
import { DropdownItem, DropdownPortal } from "./DropdownPortal";

/**
 * Props for the ControlTags component.
 */
export interface ControlTagsProps {
  /**
   * List of available tag options.
   */
  availableOptions: string[];
  /**
   * Currently selected tags (controlled).
   */
  selectedTags: string[];
  /**
   * Callback invoked with the full updated selection when tags change.
   *
   * @param selectedTags - The new set of selected tags.
   */
  onSelectionChange: (selectedTags: string[]) => void;
  /**
   * Placeholder text shown inside the input when no tags are selected.
   */
  placeholder?: string;
  /**
   * Whether the control is disabled. When true, the dropdown cannot be opened
   * and chip remove buttons are hidden; chips remain visible.
   */
  disabled?: boolean;
  /**
   * Whether creating new tags not present in `availableOptions` is allowed.
   */
  allowCreate?: boolean;
  /**
   * Optional custom CSS class applied to the outer wrapper element.
   */
  className?: string;
  /**
   * Fixed height of the chip input area. Ensures stable layout regardless of
   * content. Defaults to "80px".
   */
  height?: string;
}

/**
 * A multi-select tag control that renders selected values as chips inside a fixed-size,
 * scrollable input area. Features include: inline chip rendering inside a form-control
 * container, a persistent dropdown with checkboxes that stays open during multi-select,
 * click-outside-to-close, keyboard navigation, and optional tag creation.
 * The component is fully controlled — display derives from `selectedTags`; changes are
 * communicated via `onSelectionChange` with the full updated array.
 *
 * @param props - Component props.
 * @param props.availableOptions - Available tag options shown in the dropdown.
 * @param props.selectedTags - Currently selected tags (controlled).
 * @param props.onSelectionChange - Callback when the selection changes.
 * @param props.placeholder - Placeholder shown when no tags are selected (defaults to "Select...").
 * @param props.disabled - Disables the control, hides remove buttons, prevents dropdown (defaults to false).
 * @param props.allowCreate - Allows adding tags not in `availableOptions` (defaults to false).
 * @param props.className - Custom CSS class for the outer wrapper.
 * @param props.height - Fixed height of the input area (defaults to "80px").
 * @returns A multi-select tag input with inline chips and a persistent checkbox dropdown.
 */
export const ControlTags = ({
  availableOptions,
  selectedTags,
  onSelectionChange,
  placeholder = "Select...",
  disabled = false,
  allowCreate = false,
  className,
  height = "80px",
}: ControlTagsProps) => {
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * The control is effectively disabled when explicitly disabled OR when there
   * are no available options to populate the dropdown (and creation is not allowed).
   */
  const effectivelyDisabled = disabled || (availableOptions.length === 0 && !allowCreate);

  /**
   * Build dropdown items from the union of available options and currently-selected tags,
   * filtered by the current search text. Each item carries a `checked` flag so the dropdown
   * renders a checkbox indicating whether the tag is already selected.
   * An optional "Create" entry is prepended when `allowCreate` is on and the search text
   * is a novel value.
   */
  const dropdownItems = useMemo((): DropdownItem[] => {
    // Union of available options and any orphaned selected tags not in the options list
    const allOptions = [...new Set([...availableOptions, ...selectedTags])];
    const filtered = allOptions.filter((option) =>
      option.toLowerCase().includes(searchText.toLowerCase()),
    );

    const items: DropdownItem[] = filtered.map((option, index) => ({
      id: `option-${index}`,
      text: option,
      checked: selectedTags.includes(option),
    }));

    // Prepend a "Create" entry when allowCreate is true and the search text is a novel value
    if (
      allowCreate &&
      searchText.trim() &&
      !allOptions.some((opt) => opt.toLowerCase() === searchText.toLowerCase()) &&
      !selectedTags.includes(searchText.trim())
    ) {
      items.unshift({
        id: "create-option",
        text: `Create: "${searchText.trim()}"`,
        data: searchText.trim(),
        isCreateOption: true,
      });
    }

    return items;
  }, [availableOptions, selectedTags, searchText, allowCreate]);

  /**
   * Toggle a tag on or off. For "Create" options the new tag is added and the search
   * text is cleared. For regular items the tag is added when unchecked or removed when
   * already checked. The dropdown remains open after each interaction to support
   * multi-select without re-opening.
   */
  const handleToggleTag = useCallback(
    (item: DropdownItem) => {
      const tag = item.isCreateOption ? (item.data as string) : item.text;
      if (item.isCreateOption) {
        onSelectionChange([...selectedTags, tag]);
        setSearchText("");
      } else if (selectedTags.includes(tag)) {
        onSelectionChange(selectedTags.filter((t) => t !== tag));
      } else {
        onSelectionChange([...selectedTags, tag]);
      }
      // Dropdown intentionally stays open for multi-select
    },
    [selectedTags, onSelectionChange],
  );

  /**
   * Remove an individual tag via its chip close button.
   */
  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      onSelectionChange(selectedTags.filter((tag) => tag !== tagToRemove));
    },
    [selectedTags, onSelectionChange],
  );

  /**
   * Open the dropdown and focus the search input when the container area is clicked.
   */
  const handleContainerClick = useCallback(() => {
    if (!effectivelyDisabled) {
      setShowDropdown(true);
      inputRef.current?.focus();
    }
  }, [effectivelyDisabled]);

  /**
   * Close the dropdown and reset search state. Called by DropdownPortal on click-outside
   * and Escape key.
   */
  const handleClose = useCallback(() => {
    setShowDropdown(false);
    setSearchText("");
    setSelectedIndex(-1);
  }, []);

  return (
    <div className={`tag-multiselect ${className ?? ""}`}>
      {/* Fixed-size, scrollable form-control container holds chips and the search input */}
      <div
        ref={containerRef}
        role="combobox"
        aria-expanded={showDropdown && !effectivelyDisabled}
        aria-haspopup="listbox"
        className="form-control d-flex flex-wrap align-items-start gap-1 p-2"
        style={{
          height,
          overflowY: "auto",
          overflowX: "auto",
          cursor: effectivelyDisabled ? "default" : "text",
          backgroundColor: effectivelyDisabled ? "var(--bs-secondary-bg)" : undefined,
        }}
        onClick={handleContainerClick}
      >
        {/* Inline chip badges */}
        {selectedTags.map((tag) => (
          <Badge
            key={tag}
            bg="secondary"
            className="d-flex align-items-center"
            style={{
              cursor: effectivelyDisabled ? "default" : "pointer",
              fontSize: "0.875em",
            }}
          >
            <span>{tag}</span>
            {!effectivelyDisabled && (
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: "0.6em" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveTag(tag);
                }}
                aria-label={`Remove ${tag}`}
              />
            )}
          </Badge>
        ))}

        <input
          ref={inputRef}
          type="text"
          className="border-0 flex-grow-1"
          placeholder={selectedTags.length === 0 ? placeholder : ""}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => {
            /* istanbul ignore else */
            if (!effectivelyDisabled) setShowDropdown(true);
          }}
          disabled={effectivelyDisabled}
          autoComplete="off"
          style={{ minWidth: "80px", background: "transparent", outline: "none" }}
          aria-label="Search tags"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <DropdownPortal
        isOpen={showDropdown && dropdownItems.length > 0 && !effectivelyDisabled}
        items={dropdownItems}
        onSelect={handleToggleTag}
        onClose={handleClose}
        anchorRef={containerRef}
        selectedIndex={selectedIndex}
        onSelectedIndexChange={setSelectedIndex}
      />
    </div>
  );
};

ControlTags.displayName = "ControlTags";
